import React, { ReactElement } from 'react';
import styled, { css } from 'styled-components';
import { IIconBaseProps } from '../../common/interfaces';
import { IBadgeProps } from '../../components/Badge/Badge';
import { CtaButtonBar, ICtaButtonBarProps } from '../../components/CtaButtonBar/CtaButtonBar';
import { FeaturedIcon } from '../../components/FeaturedIcon/FeaturedIcon';
import { Heading6 } from '../../components/Headings/Headings';
import { P } from '../../components/Texts/Texts';
import { MAIN_BRAND_COLOURS } from '../../fundamentals/colours/variants';
import { IShadowProps, useShadowCss } from '../../fundamentals/shadows/shadows';
import { IBorderRadiusProps, usePanelBorderRadiusCss } from '../../fundamentals/sizes/borderRadius';
import { IMediaSizeProps, useMediaSizeCss } from '../../fundamentals/sizes/mediaSize';
import { IGapSizeProps, IGutterProps, useColumnGapCss, useGapCss, useGutterCss } from '../../fundamentals/spaces/space';
import { useDateDisplay } from '../../hooks/useDateDisplay/useDateDisplay';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseDiv, BaseImg } from '../../common/BaseElements';
import { PX_TO_REM } from '../../fundamentals/sizes/sizes';
import { useWindowDimensions } from '../../hooks/useResponsive/useResponsive';
import { useActionCss } from '../../fundamentals/actions';

export const CARD_VARIANTS = {
  ...MAIN_BRAND_COLOURS
} as const;

export type CardVariant = (typeof CARD_VARIANTS)[keyof typeof CARD_VARIANTS];

export interface ICardProps extends IShadowProps, IGutterProps, IIconBaseProps, Pick<React.HTMLProps<HTMLDivElement>, 'onClick'> {
  image?: string;
  variant: CardVariant;
  badges?: ReactElement<IBadgeProps> | ReactElement<IBadgeProps>[];
  title?: string;
  date?: Date;
  body?: ReactElement;
  ctaButtonBar?: ReactElement<ICtaButtonBarProps>;
}

export const cardColours = ({ accent, primary, status }: IColoursCollection) => ({
  cardColours: {
    border: {
      [CARD_VARIANTS.ACCENT]: { background: accent.colour },
      [CARD_VARIANTS.PRIMARY]: { background: primary.colour }
    },
    date: {
      text: status.neutral.colour
    }
  }
});

const CardContainer = styled(BaseDiv)<IShadowProps & IBorderRadiusProps>`
  ${usePanelBorderRadiusCss()};
  ${useShadowCss()};
  ${useActionCss};
  background-color: ${({ theme }) => theme.colours.eoraCoreColours.BACKGROUND_COLOR.COLOR_BACKGROUND_WIDGET};
`;

const CardImage = styled(BaseImg)<IMediaSizeProps & IBorderRadiusProps>`
  display: block;
  ${usePanelBorderRadiusCss(true)};
  width: 100%;
  ${useMediaSizeCss}
`;

const CardBorder = styled(BaseDiv)<Pick<ICardProps, 'variant' | 'image'>>`
  width: 100%;
  ${({ image, theme, variant }) =>
    !image &&
    css`
      ${usePanelBorderRadiusCss(true)};
      height: ${PX_TO_REM[8]};
      background-color: ${theme.cardColours.border[variant!].background};
    `}
`;

const CardBody = styled(BaseDiv)<IGutterProps & IGapSizeProps>`
  ${useGutterCss()};
  ${useColumnGapCss()}
  ${CtaButtonBar} {
    padding-top: ${({ theme }) => theme.spaces[8]};
  }
`;

const CardDate = styled(P)`
  color: ${({ theme }) => theme.cardColours.date.text};
`;

const CardBadges = styled(BaseDiv)<IGapSizeProps>`
  ${useGapCss()}
  row-gap: ${({ theme }) => theme.spaces[8]};
  flex-wrap: wrap;
`;

const CardTitle: React.FC<{ title?: string; isMobile: boolean }> = ({ title, isMobile }) =>
  isMobile ? (
    <P textAlign='center' textStyle='SEMI_BOLD' gutterBottom={false}>
      {title}
    </P>
  ) : (
    <Heading6 headingType='SEMI_BOLD' gutterBottom={false}>
      {title}
    </Heading6>
  );

export const Card: React.FC<ICardProps> = ({ image, variant, icon, iconType, badges, title, date, body, ctaButtonBar, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'Card');
  const { formatDate } = useDateDisplay();
  const { isMobile } = useWindowDimensions();

  return (
    <CardContainer shadow {...generateTestId()} {...rest}>
      {image && <CardImage src={image} mediaSize='wide' />}
      <CardBorder variant={variant} image={image} {...generateTestId('Border')} />
      <CardBody gutter gap={16} {...generateTestId('Body')}>
        {icon && <FeaturedIcon size='small' name={icon} type={iconType} {...generateTestId('Icon')} />}
        {badges && (
          <CardBadges gap={16} {...generateTestId('Badges')}>
            {badges}
          </CardBadges>
        )}
        {title && <CardTitle title={title} isMobile={isMobile} />}
        {date && (
          <CardDate gutterBottom={false} {...generateTestId('Date')}>
            {formatDate(date)}
          </CardDate>
        )}
        {body}
        {ctaButtonBar}
      </CardBody>
    </CardContainer>
  );
};
